import { MouseEventHandler } from 'react';
import { useEnvironment, useBi } from '@wix/yoshi-flow-editor';
import { useSettings } from '@wix/yoshi-flow-editor/tpa-settings/react';
import { maMiscClickUou } from '@wix/bi-logger-members-app-uou/v2';

import {
  BlogNavigationReference,
  ControllerProps,
  Role,
  SettingsSection,
  SettingsSectionState,
  SettingsTab,
} from '../../../../types';
import { blogContributorRoles } from '../../../../constants';
import {
  useAppSettings,
  useMember,
  useSite,
  useCanEditMember,
  useSiteHandlers,
  useBlogPosts,
} from '../../../../contexts/widgets';
import settingsParams from '../../settingsParams';

const isBlogContributor = (isBlogInstalled: boolean, memberRoles: Role[]) => {
  return (
    isBlogInstalled &&
    memberRoles.some((role) => blogContributorRoles.includes(role))
  );
};

export const shouldForceEmptyState = ({
  tabOpened,
  sectionOpened,
  sectionState,
}: ControllerProps['appSettings']) => {
  return (
    tabOpened === SettingsTab.Sections &&
    sectionOpened === SettingsSection.PostList &&
    sectionState === SettingsSectionState.Empty
  );
};

const shouldRenderBlogPosts = (
  showBlogPostsSection: boolean,
  hasBlogContent: boolean,
  hasBlogContributorPermissions: boolean,
) => {
  return (
    showBlogPostsSection && hasBlogContent && hasBlogContributorPermissions
  );
};

export const useBlogPostsRendered = () => {
  const settings = useSettings();
  const appSettings = useAppSettings();
  const { roles, blogPosts } = useMember();
  const { canEditMember } = useCanEditMember();
  const { isBlogInstalled } = useSite();

  const showBlogPostsSection = settings.get(
    settingsParams.showBlogPostsSection,
  );
  const hasBlogContent =
    canEditMember || shouldForceEmptyState(appSettings) || blogPosts.length > 0;
  const hasBlogContributorPermissions = isBlogContributor(
    isBlogInstalled,
    roles,
  );

  return {
    shouldRenderBlogPosts: shouldRenderBlogPosts(
      showBlogPostsSection,
      hasBlogContent,
      hasBlogContributorPermissions,
    ),
  };
};

const reportClickProps = maMiscClickUou({
  referralInfo: BlogNavigationReference.CreatePost,
});

export const useCreatePostClickHandler = () => {
  const bi = useBi();
  const { isPreview } = useEnvironment();
  const { createBlogPostUrl } = useBlogPosts();
  const { navigateToPath } = useSiteHandlers();

  const handleCreatePostLinkClick: MouseEventHandler = (event) => {
    bi.report(reportClickProps);

    if (isPreview) {
      event.preventDefault();
      event.stopPropagation();
      navigateToPath(createBlogPostUrl.relativeUrl);
    }
  };

  return { handleCreatePostLinkClick };
};
